import { 
  doc, getDoc, serverTimestamp, Timestamp,
  runTransaction, collection, query, where, getDocs
} from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import type { Game, User, AppConfig } from '@/lib/types';
import { logAdminActionAtomic } from '@/lib/auditLogger';
import NotificationService from './notification.service';

async function requireAppAdmin(userId: string | undefined): Promise<void> {
  if (!userId) throw new Error('User not authenticated');
  const snap = await getDoc(doc(db, 'users', userId));
  if (!snap.exists()) throw new Error('User not found');
  const user = snap.data() as User;
  if (user.roles?.app_admin !== true) throw new Error('Admin role required');
}

export async function endGame(gameId: string): Promise<void> {
  await requireAppAdmin(auth.currentUser?.uid);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');

    const game = gameSnap.data() as Game;
    if (game.status !== 'active') {
      throw new Error('Game is not active and cannot be ended');
    }

    const playerSnapshot = game.players.map((player) => ({
      userId: player.user_id,
      name: player.name,
      buyIn: player.total_bought_in,
      digitalIn: player.digital_in,
      cashIn: player.cash_in,
    }));

    const playerInputs: Record<string, { final_stack: number; cash_received: number }> = {};
    game.players.forEach((player) => {
      if (player.left_early) {
        playerInputs[player.user_id] = {
          final_stack: player.left_stack,
          cash_received: player.left_cash,
        };
      }
    });

    transaction.update(gameRef, {
      status: 'chip_entry',
      end_time: serverTimestamp(),
      chip_entry_started_at: serverTimestamp(),
      settlement: {
        gameId,
        player_snapshot: playerSnapshot,
        player_inputs: playerInputs,
        milestones: {},
        updatedAt: serverTimestamp(),
        status: 'active' as const,
      },
    });
  });
}

export async function addBuyIn(
  gameId: string,
  playerUserId: string,
  amount: number,
  type: 'digital' | 'cash' = 'digital',
  actorUserId: string,
  isCorrection: boolean = false
): Promise<void> {
  // Pre-fetch cleanup: find settlement transactions for this game
  const settlementsRef = collection(db, 'settlement_transactions');
  const q = query(settlementsRef, where('game_id', '==', gameId));
  const settlementSnap = await getDocs(q);
  const settlementRefs = settlementSnap.docs.map(doc => doc.ref);

  const gameRef = doc(db, 'games', gameId);

  let capturedPlayerName = '';

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;

    const players = [...game.players];
    const playerIdx = players.findIndex(p => p.user_id === playerUserId);
    if (playerIdx === -1) throw new Error('Player not found in game');

    const player = players[playerIdx];
    capturedPlayerName = player.name;
    const newTotalBoughtIn = player.total_bought_in + amount;

    if (newTotalBoughtIn < 0) {
      throw new Error('Total buy-in cannot be negative');
    }

    const newBuyIn = {
      amount,
      type,
      timestamp: Timestamp.now(),
      recorded_by: actorUserId,
      is_correction: isCorrection,
    };

    const updatedPlayer = {
      ...player,
      buy_ins: [...player.buy_ins, newBuyIn],
      total_bought_in: newTotalBoughtIn,
      cash_in: type === 'cash' ? player.cash_in + amount : player.cash_in,
      digital_in: type === 'digital' ? player.digital_in + amount : player.digital_in,
    };

    const final_stack = game.settlement?.player_inputs[playerUserId]?.final_stack;
    if (final_stack !== undefined) {
      // net was purged from GamePlayer
    }

    players[playerIdx] = updatedPlayer;

    // Archive stale settlement transactions
    settlementRefs.forEach(ref => transaction.update(ref, { status: 'archived' }));

    const totalPot = game.total_pot ?? game.players.reduce((sum, p) => sum + p.total_bought_in, 0);
    const totalCash = game.total_cash ?? game.players.reduce((sum, p) => sum + p.cash_in, 0);
    const totalDigital = game.total_digital ?? game.players.reduce((sum, p) => sum + p.digital_in, 0);

    const gameUpdate: Partial<Game> = {
      players,
      total_pot: totalPot + amount,
      total_cash: type === 'cash' ? totalCash + amount : totalCash,
      total_digital: type === 'digital' ? totalDigital + amount : totalDigital,
      updated_by: actorUserId,
      updated_at: serverTimestamp(),
    };

    if (isCorrection && game.settlement) {
      const settlement = { ...game.settlement };
      const snapshot = [...settlement.player_snapshot];
      const snapshotIdx = snapshot.findIndex(s => s.userId === playerUserId);
      
      if (snapshotIdx !== -1) {
        snapshot[snapshotIdx] = {
          ...snapshot[snapshotIdx],
          buyIn: newTotalBoughtIn,
          cashIn: type === 'cash' ? player.cash_in + amount : snapshot[snapshotIdx].cashIn,
          digitalIn: type === 'digital' ? player.digital_in + amount : snapshot[snapshotIdx].digitalIn,
        };
      }
      
      settlement.player_snapshot = snapshot;
      settlement.milestones = {
        ...settlement.milestones,
        cash_distribution_finalized: null,
        digital_transfers_posted: null,
        settlement_completed: null,
      };
      settlement.updatedAt = serverTimestamp();
      
      gameUpdate.settlement = settlement;
    }

    transaction.update(gameRef, gameUpdate);
  });

  let playerName = capturedPlayerName;
  if (!playerName) {
    const userSnap = await getDoc(doc(db, 'users', playerUserId));
    playerName = userSnap.exists() ? (userSnap.data() as User).name || '' : '';
  }
  if (playerName) {
    try {
      const configSnap = await getDoc(doc(db, 'config', 'app'));
      const config = configSnap.exists() ? (configSnap.data() as AppConfig) : null;
      const activeChatConfig = config?.telegram_chat_configs?.find(
        c => c.id === config?.telegram_active_chat_id
      );
      const activeChatId = activeChatConfig ? Number(activeChatConfig.chat_id) : undefined;
      if (activeChatId === undefined) {
        console.warn('[addBuyIn] No active Telegram chat configured — BUY_IN_ADDED notification skipped.');
        return;
      }

      await NotificationService.sendEvent({
        type: 'BUY_IN_ADDED',
        payload: {
          playerName,
          amount,
          type,
          chatId: activeChatId,
        },
      });
    } catch (error) {
      console.error('[addBuyIn] Notification queuing failed:', error);
    }
  }
}

export async function batchUpdateChipCounts(
  gameId: string,
  stacks: Record<string, number>,
  adminUserId: string
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);
  
  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;
  
    const players = [...game.players];
    const playerInputs = { ...game.settlement?.player_inputs };
    let changed = false;
  
    players.forEach((p, idx) => {
      const newStack = stacks[p.user_id];
      const currentStack = playerInputs[p.user_id]?.final_stack;
      if (newStack !== undefined && newStack !== currentStack) {
         players[idx] = {
           ...p,
           last_edited_at: Timestamp.now(),
           last_edited_by: adminUserId,
         };

        playerInputs[p.user_id] = {
          ...playerInputs[p.user_id],
          final_stack: newStack,
        };
        changed = true;
      }
    });
  
    if (changed) {
      transaction.update(gameRef, { 
        players, 
        'settlement.player_inputs': playerInputs 
      });
    }
  });
}

export async function markPlayerLeftEarly(
  gameId: string,
  adminUserId: string,
  playerUserId: string,
  stack: number,
  cash: number
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;

    if (game.status !== 'active') {
      throw new Error('Can only mark players as left early during an active game');
    }

    const players = [...game.players];
    const playerIdx = players.findIndex(p => p.user_id === playerUserId);
    if (playerIdx === -1) throw new Error('Player not found in game');

    const player = players[playerIdx];
    if (player.left_early) {
      throw new Error('Player is already marked as left early');
    }

    if (player.is_spectator) {
      throw new Error('Cannot mark a spectator as left early');
    }

    players[playerIdx] = {
      ...player,
      left_early: true,
      left_at: Timestamp.now(),
      left_stack: stack,
      left_cash: cash,
      last_edited_at: Timestamp.now(),
      last_edited_by: adminUserId,
    };

    transaction.update(gameRef, { players });

    logAdminActionAtomic(transaction, 'remove_player', adminUserId, { playerUserId, stack, cash }, gameId, null);
  });
}

export async function rejoinPlayer(
  gameId: string,
  adminUserId: string,
  playerUserId: string
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;

    if (game.status !== 'active') {
      throw new Error('Can only rejoin players during an active game');
    }

    const players = [...game.players];
    const playerIdx = players.findIndex(p => p.user_id === playerUserId);
    if (playerIdx === -1) throw new Error('Player not found in game');

    const player = players[playerIdx];
    if (!player.left_early) {
      throw new Error('Player is not marked as left early');
    }

    players[playerIdx] = {
      ...player,
      left_early: false,
      left_at: null,
      left_stack: 0,
      left_cash: 0,
      last_edited_at: Timestamp.now(),
      last_edited_by: adminUserId,
    };

    transaction.update(gameRef, { players });
  });
}
